import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Metric,
  WorkPeriodTense,
  WorkPeriodType,
  WorkPeriodsTab,
} from '../../containers/work-periods/work-periods.type';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import type { WorkPeriodStoreState } from './work-period-store.type';

export const useWorkPeriodStore = create<WorkPeriodStoreState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedBoardId: null,
      selectedWorkPeriods: [],
      selectedWorkPeriodType: WorkPeriodType.Defined,
      selectedTab: WorkPeriodsTab.ASSESSMENT,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      selectedMetric: Metric.Tasks,
      selectedMeasuresForComparison: [],
      selectedMeasureForOverTime: null,
      useGroups: true,

      // Context tracking
      currentOrganizationId: null,
      currentPortfolioId: null,

      // Basic setters
      setSelectedBoardId: (boardId) => set({ selectedBoardId: boardId }),
      setSelectedWorkPeriods: (workPeriods) => set({ selectedWorkPeriods: workPeriods }),
      setSelectedWorkPeriodType: (type) => set({ selectedWorkPeriodType: type }),
      setSelectedTab: (tab) => set({ selectedTab: tab }),
      setDateRange: (dateRange) => set({ dateRange: dateRange }),
      setSelectedMetric: (metric) => set({ selectedMetric: metric }),
      setSelectedMeasuresForComparison: (measures) => set({ selectedMeasuresForComparison: measures }),
      setSelectedMeasureForOverTime: (measure) => set({ selectedMeasureForOverTime: measure }),
      setUseGroups: (useGroups) => set({ useGroups }),

      // Context change handler
      checkAndResetOnContextChange: (organization, portfolio) => {
        const { currentOrganizationId, currentPortfolioId } = get();
        const newOrganizationId = organization?.id || null;
        const newPortfolioId = portfolio?.id || null;

        // Check if organization or portfolio has changed
        if (currentOrganizationId !== newOrganizationId || currentPortfolioId !== newPortfolioId) {
          // Reset all selections
          set({
            selectedBoardId: null,
            selectedWorkPeriods: [],
            selectedWorkPeriodType: WorkPeriodType.Defined,
            selectedTab: WorkPeriodsTab.ASSESSMENT,
            dateRange: {
              startDate: null,
              endDate: null,
            },
            selectedMetric: Metric.Tasks,
            selectedMeasuresForComparison: [],
            selectedMeasureForOverTime: null,
            useGroups: true,
            currentOrganizationId: newOrganizationId,
            currentPortfolioId: newPortfolioId,
          });
        } else {
          // Just update the context tracking without resetting
          set({
            currentOrganizationId: newOrganizationId,
            currentPortfolioId: newPortfolioId,
          });
        }
      },

      // Helper actions
      addWorkPeriod: (workPeriod) => {
        const { selectedWorkPeriods } = get();
        // Limit to 7 work periods for comparison view
        const isAlreadySelected = selectedWorkPeriods.some((wp) => wp.id === workPeriod.id);
        if (isAlreadySelected) {
          return;
        }

        // Limit to 7 work periods for comparison view
        if (selectedWorkPeriods.length >= 7) {
          const newWorkPeriods = [...selectedWorkPeriods.slice(1), workPeriod];
          set({ selectedWorkPeriods: newWorkPeriods });
          return;
        }

        set({ selectedWorkPeriods: [...selectedWorkPeriods, workPeriod] });
      },

      removeWorkPeriod: (workPeriodId) => {
        const { selectedWorkPeriods } = get();
        const filteredWorkPeriods = selectedWorkPeriods.filter((wp) => wp.id !== workPeriodId);
        set({ selectedWorkPeriods: filteredWorkPeriods });
      },

      clearWorkPeriods: () => set({ selectedWorkPeriods: [] }),

      setBoardIdAndAutoSelectWorkPeriod: (boardId, workPeriods) => {
        set({ selectedBoardId: boardId, selectedWorkPeriods: [] });

        if (boardId && workPeriods && workPeriods.length > 0) {
          // Find the most recent uncompleted work period
          // Uncompleted = time_tense is not 'past' (so 'present' or 'future' or undefined)
          const uncompletedWorkPeriods = workPeriods.filter((wp) => wp.time_tense !== WorkPeriodTense.Past);

          if (uncompletedWorkPeriods.length > 0) {
            // Sort by start_date descending to get the most recent
            const sortedUncompletedWorkPeriods = uncompletedWorkPeriods.sort(
              (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
            );

            // Select the most recent uncompleted work period
            const mostRecentUncompleted = sortedUncompletedWorkPeriods[0];
            set({ selectedWorkPeriods: [mostRecentUncompleted] });
          } else if (workPeriods.length > 0) {
            // If no uncompleted work periods, select the most recent one overall
            const sortedWorkPeriods = [...workPeriods].sort(
              (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
            );
            set({ selectedWorkPeriods: [sortedWorkPeriods[0]] });
          }
        }
      },

      resetStore: () =>
        set({
          selectedBoardId: null,
          selectedWorkPeriods: [],
          selectedWorkPeriodType: WorkPeriodType.Defined,
          selectedTab: WorkPeriodsTab.ASSESSMENT,
          dateRange: {
            startDate: null,
            endDate: null,
          },
          selectedMetric: Metric.Tasks,
          selectedMeasuresForComparison: [],
          selectedMeasureForOverTime: null,
          useGroups: true,
          currentOrganizationId: null,
          currentPortfolioId: null,
        }),
    }),
    {
      name: 'work-period-store',
      storage: createSafeJSONStorage(() => localStorage),
      // Only persist the selections, not any fetched data
      partialize: (state) => ({
        selectedBoardId: state.selectedBoardId,
        selectedWorkPeriods: state.selectedWorkPeriods,
        selectedWorkPeriodType: state.selectedWorkPeriodType,
        selectedTab: state.selectedTab,
        dateRange: state.dateRange,
        selectedMetric: state.selectedMetric,
        selectedMeasuresForComparison: state.selectedMeasuresForComparison,
        selectedMeasureForOverTime: state.selectedMeasureForOverTime,
        useGroups: state.useGroups,
        currentOrganizationId: state.currentOrganizationId,
        currentPortfolioId: state.currentPortfolioId,
      }),
    },
  ),
);
